import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "chart",
    "stat",
    "toggle",
    "postItem",
    "chartBar",
    "lastUpdated",
  ];
  static values = {
    data: Object,
    refreshInterval: Number,
  };

  connect() {
    this.animateStats();
    this.setupRefresh();
    this.initializeCharts();
  }

  disconnect() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  animateStats() {
    this.statTargets.forEach((target) => {
      const finalValue = parseInt(target.textContent);
      let currentValue = 0;
      const increment = Math.ceil(finalValue / 20);
      const timer = setInterval(() => {
        currentValue += increment;
        if (currentValue >= finalValue) {
          currentValue = finalValue;
          clearInterval(timer);
        }
        target.textContent = currentValue.toLocaleString();
      }, 50);
    });
  }

  setupRefresh() {
    if (this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.refreshDashboard();
      }, this.refreshIntervalValue * 1000);
    }
  }

  async refresh() {
    await this.refreshDashboard(true);
  }

  async refreshDashboard(manual = false) {
    try {
      const response = await fetch(window.location.href, {
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Update stats
        this.updateStats(doc);

        // Update last updated time if manual
        if (manual && this.hasLastUpdatedTarget) {
          const now = new Date();
          const hours12 = ((now.getHours() + 11) % 12) + 1; // convert to 12h
          const minutes = now.getMinutes().toString().padStart(2, "0");
          const ampm = now.getHours() >= 12 ? "PM" : "AM";
          this.lastUpdatedTarget.textContent = `Last updated: ${hours12}:${minutes} ${ampm}`;
        }

        // Show refresh indicator
        this.showRefreshIndicator();
      }
    } catch (error) {
      console.error("Failed to refresh dashboard:", error);
    }
  }

  updateStats(doc) {
    this.statTargets.forEach((target, index) => {
      const newStat = doc.querySelectorAll('[data-dashboard-target="stat"]')[
        index
      ];
      if (newStat && target.textContent !== newStat.textContent) {
        target.classList.add("animate-pulse");
        setTimeout(() => {
          target.textContent = newStat.textContent;
          target.classList.remove("animate-pulse");
        }, 300);
      }
    });
  }

  showRefreshIndicator() {
    const indicator = document.createElement("div");
    indicator.className =
      "fixed top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-lg text-sm z-50 transition-opacity";
    indicator.textContent = "Dashboard updated";
    document.body.appendChild(indicator);

    // Fade out after showing
    setTimeout(() => {
      indicator.style.opacity = "0";
      setTimeout(() => {
        indicator.remove();
      }, 300);
    }, 2000);
  }

  initializeCharts() {
    this.chartBarTargets.forEach((bar, index) => {
      setTimeout(() => {
        bar.style.transform = "scaleX(1)";
      }, index * 100);
    });
  }

  toggle(event) {
    const target = event.currentTarget;
    const section = target.closest(".dashboard-section");
    const content = section.querySelector(".dashboard-content");

    if (content.classList.contains("hidden")) {
      content.classList.remove("hidden");
      target.querySelector("svg").style.transform = "rotate(180deg)";
    } else {
      content.classList.add("hidden");
      target.querySelector("svg").style.transform = "rotate(0deg)";
    }
  }

  // Filter functionality
  filterPosts(event) {
    const filter = event.target.value;
    const posts = this.postItemTargets;

    posts.forEach((post) => {
      if (filter === "all" || post.dataset.status === filter) {
        post.style.display = "block";
      } else {
        post.style.display = "none";
      }
    });

    // Show feedback
    this.showFilterFeedback(filter, posts);
  }

  showFilterFeedback(filter, posts) {
    const visibleCount = posts.filter(
      (post) => filter === "all" || post.dataset.status === filter
    ).length;

    const feedback = document.createElement("div");
    feedback.className =
      "fixed bottom-4 right-4 bg-primary-600 dark:bg-primary-500 text-white px-4 py-2 rounded-lg text-sm z-50 transition-opacity";
    feedback.textContent = `Showing ${visibleCount} ${
      filter === "all" ? "" : filter
    } posts`;
    document.body.appendChild(feedback);

    setTimeout(() => {
      feedback.style.opacity = "0";
      setTimeout(() => {
        feedback.remove();
      }, 300);
    }, 2000);
  }

  // Search functionality
  searchPosts(event) {
    const query = event.target.value.toLowerCase();
    const posts = this.postItemTargets;

    posts.forEach((post) => {
      const title = post.querySelector(".post-title").textContent.toLowerCase();
      const author = post
        .querySelector(".post-author")
        .textContent.toLowerCase();

      if (title.includes(query) || author.includes(query)) {
        post.style.display = "block";
      } else {
        post.style.display = "none";
      }
    });
  }
}
